import { useStore } from 'hooks/useStore';
import {
  DrugReferenceFgNdcToBulkNdc,
  DrugReferenceFgNdcToBulkNdcPayload
} from 'Interfaces/ReferenceInterfaces/DrugReferenceFgNdcToBulkNdcInterface';
import { useEffect, useRef, useState } from 'react';
import { DataTableStateEvent } from 'primereact/datatable';
import { debounce } from 'lodash';
import { useInterval } from 'hooks/usePolling';
import { getJitteredInterval } from 'utils/pollingUtility';
import * as XLSX from 'xlsx';
import {toast} from 'react-toastify';

interface FormData {
  fg_ndc: string;
  bulk_ndc: string;
  description: string;
  primary_ingredient: string;
  supplier: string;
  supplier_name: string;
  dosage_form: string;
  coating: number | null;
  product_color: string;
}

export const useDrugReferenceFgNdcToBulkNdcTable = (pageSize: number = 100) => {
  const { drugReferenceFgNdcToBulkNdcStore, coatingTypeStore } = useStore();

  const [selectedRow, setSelectedRow] = useState<DrugReferenceFgNdcToBulkNdc | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const itemMenu = useRef<any>(null);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const POLLING_INTERVAL = getJitteredInterval();

  const dosageFormOptions = [
    {
      label: 'Capsule',
      value: 'Capsule'
    },
    {
      label: 'Tablet',
      value: 'Tablet'
    },
    {
      label: 'Any',
      value: 'Any'
    }
  ];

  // Load initial data when component mounts
  useEffect(() => {
    drugReferenceFgNdcToBulkNdcStore.fetchDrugReferenceFgNdcToBulkNdcs(
      currentPage,
      pageSize,
      searchQuery || undefined
    );

    // Fetch coating types for dropdown in the add/edit modal
    if (coatingTypeStore.coatingTypes.length === 0) {
      coatingTypeStore.fetchCoatingTypes();
    }
  }, [currentPage, pageSize, searchQuery]);

  // Set up polling for data updates
  useInterval(async () => {
    try {
      await drugReferenceFgNdcToBulkNdcStore.pollDrugReferenceFgNdcToBulkNdcs(
        currentPage,
        pageSize,
        searchQuery || undefined
      );
    } catch (error) {
      // Don't break the polling cycle on error
      if(environment === 'development'){
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling for Drug Reference FG NDC to Bulk NDC data!');
    }
  }, POLLING_INTERVAL);

  // Handle page change
  const onPageChange = (event: DataTableStateEvent) => {
    if (event.page !== undefined) {
      const newPage = event.page + 1; // PrimeReact uses 0-based indexing, our API uses 1-based
      setCurrentPage(newPage);
      drugReferenceFgNdcToBulkNdcStore.fetchDrugReferenceFgNdcToBulkNdcs(
        newPage,
        pageSize,
        searchQuery || undefined
      );
    }
  };

  // Debounced search function
  const debouncedSearch = debounce((query: string) => {
    setSearchQuery(query);
    drugReferenceFgNdcToBulkNdcStore.fetchDrugReferenceFgNdcToBulkNdcs(
      1,
      pageSize,
      query || undefined
    );
    setCurrentPage(1); // Reset to first page on new search
  }, 500);

  const handleSearchChange = (query: string) => {
    debouncedSearch(query);
  };

  // Prepare form data for editing
  const prepareFormData = (row: DrugReferenceFgNdcToBulkNdc): FormData => {
    return {
      fg_ndc: row.fg_ndc || '',
      bulk_ndc: row.bulk_ndc || '',
      description: row.description || '',
      primary_ingredient: row.primary_ingredient || '',
      supplier: row.supplier || '',
      supplier_name: row.supplier_name || '',
      dosage_form: row.dosage_form || '',
      coating: row.coating?.id || null,
      product_color: row.product_color || ''
    };
  };

  const handleAddRow = async (formData: FormData): Promise<void> => {
    if (selectedRow) {
      // Edit existing row - only update the three allowed fields
      const updatePayload: DrugReferenceFgNdcToBulkNdcPayload = {
        fg_ndc: selectedRow.fg_ndc || '',
        bulk_ndc: selectedRow.bulk_ndc || '',
        description: selectedRow.description || '',
        primary_ingredient: selectedRow.primary_ingredient || '',
        supplier: selectedRow.supplier || '',
        supplier_name: selectedRow.supplier_name || '',
        dosage_form: formData.dosage_form,
        coating: formData.coating,
        product_color: formData.product_color
      };

      const success = await drugReferenceFgNdcToBulkNdcStore.updateDrugReferenceFgNdcToBulkNdc(
        selectedRow.id,
        updatePayload
      );
      if (!success) {
        throw new Error('Failed to update drug reference FG NDC to bulk NDC');
      }
    } else {
      // Add new row - all fields are included
      const createPayload: DrugReferenceFgNdcToBulkNdcPayload = {
        fg_ndc: formData.fg_ndc,
        bulk_ndc: formData.bulk_ndc,
        description: formData.description,
        primary_ingredient: formData.primary_ingredient,
        supplier: formData.supplier,
        supplier_name: formData.supplier_name,
        dosage_form: formData.dosage_form,
        coating: formData.coating,
        product_color: formData.product_color,
        flag: true
      };

      const success =
        await drugReferenceFgNdcToBulkNdcStore.createDrugReferenceFgNdcToBulkNdc(createPayload);
      if (!success) {
        throw new Error('Failed to create drug reference FG NDC to bulk NDC');
      }
    }
    setSelectedRow(null);
    // Refresh data while maintaining current page
    await drugReferenceFgNdcToBulkNdcStore.fetchDrugReferenceFgNdcToBulkNdcs(
      currentPage,
      pageSize,
      searchQuery || undefined
    );
  };

  const handleDeleteRow = async (): Promise<void> => {
    if (selectedRow) {
      const success = await drugReferenceFgNdcToBulkNdcStore.deleteDrugReferenceFgNdcToBulkNdc(
        selectedRow.id
      );
      if (!success) {
        throw new Error('Failed to delete drug reference FG NDC to bulk NDC');
      }
      setSelectedRow(null);
      // Refresh data while maintaining current page
      await drugReferenceFgNdcToBulkNdcStore.fetchDrugReferenceFgNdcToBulkNdcs(
        currentPage,
        pageSize,
        searchQuery || undefined
      );
    }
  };

  const refreshData = async () => {
    await drugReferenceFgNdcToBulkNdcStore.fetchDrugReferenceFgNdcToBulkNdcs(
      currentPage,
      pageSize,
      searchQuery || undefined
    );
  };

  const handleExportToExcel = () => {
    const filteredData = drugReferenceFgNdcToBulkNdcStore.drugReferenceFgNdcToBulkNdcs.filter(
      item => {
        if (!searchQuery.trim()) {
          return true;
        }
        const searchLower = searchQuery.toLowerCase();
        return (
          item.fg_ndc?.toLowerCase().includes(searchLower) ||
          item.bulk_ndc?.toLowerCase().includes(searchLower) ||
          item.description?.toLowerCase().includes(searchLower) ||
          item.primary_ingredient?.toLowerCase().includes(searchLower) ||
          item.supplier?.toLowerCase().includes(searchLower) ||
          item.supplier_name?.toLowerCase().includes(searchLower) ||
          item.dosage_form?.toLowerCase().includes(searchLower) ||
          item.product_color?.toLowerCase().includes(searchLower)
        );
      }
    );

    const exportData = filteredData.map(item => ({
      'FG NDC': item.fg_ndc,
      'Bulk NDC': item.bulk_ndc,
      Description: item.description,
      'Primary Ingredient': item.primary_ingredient,
      Supplier: item.supplier,
      'Supplier Name': item.supplier_name,
      'Dosage Form': item.dosage_form,
      Coating: item.coating ? item.coating.description : 'N/A',
      'Product Color': item.product_color
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Drug Ref Fg To Bulk');
    XLSX.writeFile(workbook, 'drug_ref_fg_to_bulk.xlsx');
  };

  return {
    drugReferenceData: drugReferenceFgNdcToBulkNdcStore.drugReferenceFgNdcToBulkNdcs,
    isLoading: drugReferenceFgNdcToBulkNdcStore.isLoading,
    pagination: drugReferenceFgNdcToBulkNdcStore.pagination,
    coatingOptions: [
      { label: 'N/A', value: null },
      ...coatingTypeStore.coatingTypes.map(coating => ({
        label: coating.description || '',
        value: coating.id
      }))
    ],
    isLoadingCoatingTypes: coatingTypeStore.isLoading,
    searchQuery,
    itemMenu,
    selectedRow,
    showAddModal,
    showDeleteModal,
    setSelectedRow,
    setShowAddModal,
    setShowDeleteModal,
    handleAddRow,
    handleDeleteRow,
    handleSearchChange,
    prepareFormData,
    onPageChange,
    currentPage,
    dosageFormOptions,
    refreshData,
    handleExportToExcel
  };
};
